<script>
  import { show_message } from '../lib/flash';
  import ChatBanner from './ChatBanner.svelte';
  import { MDCTextField } from '@material/textfield';
  import router from '../lib/router';
  import { onMount, onDestroy, tick } from 'svelte';
  import moment from 'moment';
  import TableOrder from '../orders/orderDetails/components/TableOrder.svelte';
  import { t9n } from '../lib/translate.js';
  import settings from '../config';
  import session from '../lib/session';
  import { TS } from '../config/translation-keys.js';
  import APIClient from '../state-charts/api-client';
  import ChatMessage from './ChatMessage.svelte';
  import { fade } from 'svelte/transition';
  import { MDCDialog } from '@material/dialog';
  import Cropper from 'cropperjs';
  import { chat_count } from './unread_chats_count.js';

  // component options  ------------------------------------------------------ //

  export let route_name;
  export let external_order_uuid = null;
  export let external_partner = null;
  export let applicant_index = null;

  // internal variables ------------------------------------------------------ //

  let message_list;
  let message;
  let messages = [];
  let unread_count = 0;
  let order;
  let partner_role;
  let partner_profile;
  let show_partners = true;
  let disabled = false;
  let internal_uuid;
  let order_status;
  let order_status_raw;
  let first_time = false;
  let send_with_enter_value;
  let visible = true;

  let num_of_pages;
  let per_page = 9;
  let current_page = 1;
  let max_chat_partners = 50;
  let searched;
  let field_search;
  let disable_pager;
  let search_disable;

  let partner_id;
  let all_chats;
  let all_other_workers;
  let message_my_profile;
  let message_partner_profile;

  let default_image = `/uploads/profile_images/default-avatar.jpg`;
  let cropper;
  let dialog_pic;
  let picture_src;
  let img_canvas = document.createElement('canvas');

  let goto_partners_lab = t9n(TS.BUTTON_BACK_TO_CHAT_LIST);
  let goto_order_lab = t9n(TS.BUTTON_GO_TO_ORDER);
  let no_chats_yet = t9n(TS.PRIVATE_CHAT_NO_DISCUSSION_YET);
  let no_chats_yet_in_partner_panel = t9n(TS.PRIVATE_CHAT_NO_DISCUSSION_YET);
  let input_placeholder = t9n(TS.CHAT_INPUT_PLACEHOLDER);
  let order_status_label = t9n(TS.ORDER_STATUS);
  let other_workers_label = t9n(TS.OTHER_WORKERS_FOR_THIS_ORDER);
  let send_with_enter_label = t9n(TS.SEND_WITH_ENTER_BUTTON);
  let search_label = t9n(TS.CHAT_SEARCH_FIELD);
  let temp_search_tasks = t9n(TS.ORDER_TASK_DESC);
  let error_chat_self_message = t9n(TS.ERROR_CHAT_SELF_MESSAGE);
  let order_not_available = t9n(TS.CHAT_ORDER_HAS_BEEN_TAKEN);
  let chat_order_has_been_taken_orderer = t9n(
    TS.CHAT_ORDER_HAS_BEEN_TAKEN_ORDERER
  );
  let chat_goto_unread_label = t9n(TS.CHAT_GOTO_UNREAD_LABEL);
  let chat_new_label = t9n(TS.CHAT_NEW_LABEL);
  let chat_upload_image = t9n(TS.CHAT_UPLOAD_IMAGE);
  let chat_upload_image_button_label = t9n(TS.CHAT_UPLOAD_IMAGE_BUTTON);
  let close_label = t9n(TS.ORDERER_TEAMS_CLOSE_LABEL);
  let invalid_img = t9n(TS.CHAT_INVALID_IMG);
  let image_label = t9n(TS.CHAT_IMAGE_LABEL);
  let chat_start_cropping = t9n(TS.CHAT_START_CROPPING);
  let dialog_title = chat_upload_image;

  temp_search_tasks = temp_search_tasks.toLowerCase();
  chat_new_label = chat_new_label.toUpperCase();

  // utility functions ------------------------------------------------------- //

  const create_message_profiles = () => {
    message_my_profile = {
      image_url: $session.user.image_url,
      message_role: partner_profile.role, //this is your role due to an exception in the api data structure
      name: $session.user.first_name,
      id: $session.user.id,
    };

    message_partner_profile = {
      image_url: partner_profile.image_url,
      message_role: partner_profile.role === 'orderer' ? 'worker' : 'orderer',
      message_role_translated:
        partner_profile.role === 'orderer'
          ? t9n(TS.FIRST_TIME_WORKER_BUTTON_LABEL)
          : t9n(TS.FIRST_TIME_ORDERER_BUTTON_LABEL),
      name: partner_profile.name,
      id: partner_profile.partner_id,
    };
  };

  const add_sender_data = message => {
    let not_me = message.sender !== partner_profile.role;
    message.sender = not_me ? message_partner_profile : message_my_profile;
    return message;
  };

  const get_messages = async () => {
    const data = { order_uuid: internal_uuid, partner_id: partner_id };
    const result = await APIClient.get_chat_messages(data);

    if (!!result) {
      if (messages.length !== result.chats.length) {
        messages = result.chats.map(add_sender_data);

        // this group_position numbering allows at the ChatMessage component to hide
        // the avatar img if it is a consecutive message sent within 10s by the same user
        for (let i = 0; i < messages.length - 1; i++) {
          const x = messages[i];
          const y = messages[i + 1];
          const same_user = x.sender === y.sender;
          const within_ten_sec =
            moment(x.created_at).add(10, 's') >= moment(y.created_at);
          x.group_position = x.group_position || 0;
          if (same_user && within_ten_sec) {
            x.group = true;
            y.group = true;
            y.group_position = x.group_position + 1;
          } else {
            y.group_position = 0;
          }
        }

        add_html_new_marker();

        await tick();
        setTimeout(() => check_height(), 1000);
        scroll_to_last_message();

        await denote_reviewed_messages();
      }
    }
  };

  // this adds the new label marker for the html rendering
  const add_html_new_marker = () => {
    for (let i = 0; i < messages.length - 1; i++) {
      if (messages[i].read === false) {
        messages[i].new_label = true;
        return;
      }
    }
  };

  const send_message = async () => {
    const data = {
      order_uuid: order.order_uuid,
      message: message,
      partner_id: partner_id,
    };
    await APIClient.send_chat_message(data);
    message = null;
    if (first_time) {
      await open_chat(internal_uuid, partner_id); // this is needed to refresh the chat with the right profiles
      first_time = false;
    }
    await get_messages();
  };

  const denote_reviewed_messages = async () => {
    const unread = messages.filter(m => m.read === false);

    if (order) {
      // edge case: not chosen worker wrote a message that orderer have not read will remain in forever unread state
      if (unread.length > 0) {
        chat_count.update(n => n - unread.length);
        if ($chat_count < 0) chat_count.set(0);
        let data = {
          order_uuid: order.order_uuid,
          chat_ids: unread.map(m => m.chat_id),
        };

        await APIClient.read_chat_messages(data);
      }
    } else if (disabled) {
      if (unread.length > 0) {
        chat_count.update(n => n - unread.length);
        if ($chat_count < 0) chat_count.set(0);
        let data = {
          order_uuid: internal_uuid,
          chat_ids: unread.map(m => m.chat_id),
        };
        setTimeout(async () => await APIClient.read_chat_messages(data), 1000);
      }
    }
  };

  const scroll_to_last_message = () => {
    message_list.scrollTop = message_list.scrollHeight;
    visible = is_element_visible();
  };

  const is_element_visible = (el, holder) => {
    el = el || document.querySelector('.chat-message__date_new');
    holder = holder || message_list;
    if (el) {
      const { top, bottom, height } = el.getBoundingClientRect();
      const holderRect = holder.getBoundingClientRect();

      return top <= holderRect.top
        ? holderRect.top - top <= height
        : bottom - holderRect.bottom <= height;
    }
  };

  const goto_message = () => {
    let new_message_start = document.querySelector('.chat-message__date_new');
    new_message_start.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
    visible = true;
  };

  const get_order = async order_uuid => {
    try {
      order = await APIClient.get_order_with_errors({ order_uuid: order_uuid });
      order_status_raw = order.status;
      order_status = t9n(order_status_raw);
    } catch (e) {
      if (
        e.details.error_data[0].code === 'error_not_found' ||
        e.error_data[0].code === 'error_not_found'
      ) {
        return true;
      } else {
        order = await APIClient.get_order({ order_uuid: order_uuid });
        return false;
      }
    }
  };

  const get_partners = async () => {
    let results = await get_chat_summaries(per_page);
    num_of_pages = results.results_info.number_of_pages;
    all_chats = results.chats;
    search_disable = all_chats;
  };

  const get_other_workers = async () => {
    let _per_page = 50;
    let results = await get_chat_summaries(_per_page, internal_uuid);
    all_other_workers = results.chats;
  };

  const get_chat_summaries = async (_per_page, _uuid) => {
    let api_data;
    if (_uuid) {
      api_data = {
        number_of_items_per_page: _per_page,
        order_uuid: _uuid,
      };
    } else {
      api_data = {
        page: current_page,
        number_of_items_per_page: _per_page,
      };
    }
    return await APIClient.get_chat_summary(api_data);
  };

  const get_profile_data = () => {
    partner_role = $session.user.last_role === 'worker' ? 'orderer' : 'worker';
    if (order.hasOwnProperty(partner_role)) {
      if (order[partner_role].hasOwnProperty('basic_profile')) {
        partner_profile = order[partner_role].basic_profile;
        partner_profile.role = $session.user.last_role;
        partner_profile.name = `${partner_profile.first_name} ${partner_profile.last_name}`;
        partner_profile.partner_id = partner_id;
      }
    } else {
      if (order.applications) {
        partner_profile =
          order.applications[applicant_index].worker.basic_profile;
        partner_profile.role = $session.user.last_role;
        partner_profile.name = `${partner_profile.first_name} ${partner_profile.last_name}`;
        partner_profile.partner_id = partner_id;
        applicant_index = null;
      } else {
        show_message('NO SUCH PROFILE', 'error');
      }
    }
  };

  /**
   * the main function in this component
   * it decides whether the partner is in the chat summery list if not this is the
   * first time you two talk and creates the profiles based on the order, not from the chat_summary api
   */
  const open_chat = async (uuid, partner) => {
    visible = true;
    all_other_workers = null;
    message = null;
    partner_profile = null;
    messages = [];
    disabled = false;
    internal_uuid = uuid;
    input_placeholder = t9n(TS.CHAT_INPUT_PLACEHOLDER);
    partner_id = Number(partner);
    let open_chat_without_order_bool = false;
    open_chat_without_order_bool = await get_order(internal_uuid);
    if (open_chat_without_order_bool === true) {
      await open_chat_without_order();
      return;
    }

    let results = await get_chat_summaries(max_chat_partners, internal_uuid);

    if (results.chats.length === 0 || applicant_index) {
      get_profile_data();
      first_time = true;
    } else {
      partner_profile = results.chats.find(x => x.partner_id === partner_id);
      first_time = false;
    }

    if (partner_id === $session.user.id) {
      show_message(error_chat_self_message, 'error');
      await show_partners_panel();
    }

    if (order?.you_have_applied !== true) {
      if (order.hasOwnProperty('worker')) {
        if (order.worker.hasOwnProperty('basic_profile')) {
          if (
            order.worker.basic_profile.id !== partner_id &&
            order.worker.basic_profile.id !== $session.user.id
          ) {
            // #371
            await open_chat_with_selected_worker();
            return;
          }
        }
      }
      if (!order.hasOwnProperty('worker')) {
        // "worker sends chat to orderer (worker has not applied),
        // oderer opens the chat and opens the worker profile from chat -> error" fix:
        order_status_raw = 'order_status_completed';
      }
    }
    trigger_banner_effects(); //adds
    create_message_profiles();
    await get_messages();
    if (order.hasOwnProperty('applications')) {
      if (order.applications.length > 1) {
        await get_other_workers();
      }
    }
    show_partners = false;
    trigger_banner_effects(); //removes
  };

  const open_chat_without_order = async () => {
    disabled = true;
    order_status = order_not_available;
    // trick to not open the profile in ChatBanners:
    order_status_raw = 'order_status_completed';
    order = null;
    let results = await get_chat_summaries(max_chat_partners, internal_uuid);
    partner_profile = results.chats.find(x => x.partner_id === partner_id);
    first_time = false;
    trigger_banner_effects(); //adds
    create_message_profiles();
    await get_messages();
    show_partners = false;
    trigger_banner_effects(); //removes
  };

  const open_chat_with_selected_worker = async () => {
    disabled = true;
    order_status = chat_order_has_been_taken_orderer;
    // trick to not open the profile in ChatBanners:
    order_status_raw = 'order_status_completed';
    order = null;
    trigger_banner_effects(); //adds
    create_message_profiles();
    await get_messages();
    show_partners = false;
    trigger_banner_effects(); //removes
  };

  const show_partners_panel = async () => {
    await get_partners();
    show_partners = true;
    setTimeout(set_partner_panel_opacity, 100);
    setTimeout(init_search_field, 150);
  };

  const set_partner_panel_opacity = () => {
    document.getElementById('partners_container').style.opacity = 1;
  };

  const to_order = () => {
    router.navigate(`/order-details?id=${internal_uuid}&chat=${partner_id}`);
  };

  const prev_page = async () => {
    if (current_page !== 1) {
      current_page--;
    }
    await get_partners();
  };

  const next_page = async () => {
    if (current_page !== num_of_pages) {
      current_page++;
    }
    await get_partners();
  };

  const trigger_banner_effects = () => {
    let user_name = document.getElementById('user_name');
    let user_info = document.getElementById('user_info');
    let profile_image = document.getElementById('profile_image');
    if (!!user_name) {
      if (user_name.classList.contains('slideInRight')) {
        user_name.classList.remove('slideInRight');
        user_info.classList.remove('slideUp');
        profile_image.classList.remove('slideInLeft');
      } else {
        user_name.classList.add('slideInRight');
        user_info.classList.add('slideUp');
        profile_image.classList.add('slideInLeft');
      }
    }
  };

  const filter_results = async e => {
    if (e.code === 'Enter') {
      searched = e.target.value;

      let search_regex = new RegExp(searched, 'i'); // 'i' makes the RegExp ignore case
      if (searched !== '') {
        let api_data = { page: 1, number_of_items_per_page: 50 };
        let results = await APIClient.get_chat_summary(api_data);
        all_chats = results?.chats.filter(function(e) {
          return (
            search_regex.test(e.order_description) || search_regex.test(e.name)
          );
        });
        no_chats_yet_in_partner_panel = t9n(TS.TABLE_NO_RESULT);
        disable_pager = true;
      } else {
        await get_partners();
        no_chats_yet_in_partner_panel = t9n(TS.PRIVATE_CHAT_NO_DISCUSSION_YET);
        disable_pager = false;
      }
    }
  };

  const check_height = () => {
    let images = document.querySelectorAll('.chat-image');
    images.forEach(item => {
      if (item.height > 400) {
        item.parentElement.classList.add('overflow-fade');
      }
    });
  };

  const init_search_field = () => {
    field_search = new MDCTextField(document.querySelector('.field-search'));
  };

  const debounce_input = (fn, delay) => {
    let timer = null;
    return function() {
      let context = this,
        args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function() {
        fn.apply(context, args);
      }, delay);
    };
  };

  // handling `Enter` key in the message editor ------------------------------ //

  const set_send_message_with_enter_event = event => {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        return;
      }
      document.getElementById('send_button').click();
      return false;
    }
  };

  const send_message_with_enter = async () => {
    let data = { send_message_with_enter: true };
    await APIClient.update_profile_without_reload(data);
    // send_with_enter_value = $session.user.send_message_with_enter;
    send_with_enter_value = true;

    document
      .querySelector('#chat_textarea')
      .addEventListener('keydown', set_send_message_with_enter_event, true);
  };

  const send_message_with_button = async () => {
    let data = { send_message_with_enter: false };
    await APIClient.update_profile_without_reload(data);
    // send_with_enter_value = $session.user.send_message_with_enter;
    send_with_enter_value = false;

    document
      .querySelector('#chat_textarea')
      .removeEventListener('keydown', set_send_message_with_enter_event, true);
  };

  const handle_switch_with_enter_click = async () => {
    return send_with_enter_value
      ? await send_message_with_button()
      : await send_message_with_enter();
  };

  // upload image ------------------------------------------------------------- //

  const load_file = () => {
    let file = document.querySelector('input[type=file]').files[0];
    let reader = new FileReader();

    reader.onload = function() {
      if (file.size > 2097152) {
        show_message(invalid_img, 'error');
      } else {
        show_preview(reader.result);
      }
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  function show_preview(src) {
    dialog_pic = new MDCDialog(document.getElementById(`pic-dialog`));
    dialog_pic.open();

    let preview = document.getElementById('img_preview');

    preview.src = src;
    preview.style.display = 'block';
    preview.style.width = '464px';
    preview.style.maxWidth = '100%';

    cropper = new Cropper(preview, {
      autoCrop: false,
      background: false,
    });

    cropper.disable();
  }

  const post_chat_image = async () => {
    let canvas = cropper.getCroppedCanvas();
    let base64 = canvas.toDataURL('image/jpeg', 0.5);
    base64 = base64.substring(base64.indexOf(',') + 1);

    const data = {
      order_uuid: order.order_uuid,
      base64_binary: base64,
      partner_id: partner_id,
    };

    await APIClient.send_chat_image(data);
    await get_messages();

    dialog_pic.close();
    document.querySelector('input[type=file]').value = '';
    cropper.destroy();
  };

  const start_cropping = () => {
    cropper.destroy();
    let preview = document.getElementById('img_preview');

    cropper = new Cropper(preview, {
      autoCrop: true,
      background: false,
    });

    cropper.enable();
  };

  const close_upload_dialog = () => {
    document.querySelector('input[type=file]').value = '';
    dialog_pic.close();
    cropper.destroy();
  };

  // set moment.js's language ------------------------------------------------- //

  const set_moment_language = () => {
    if ($session.language === 'fi') {
      function processRelativeTime(number, withoutSuffix, key, isFuture) {
        var format = {
          m: ['minuutti', 'minuutin'],
          mm: [number + ' minuuttia', number + ' minuutin'],
          h: ['tunti', 'tunnin'],
          hh: [number + ' tuntia', number + ' tunnin'],
          d: ['päivä', 'päivän'],
          dd: [number + ' päivää', number + ' päivän'],
          M: ['kuukausi', 'kuukauden'],
          MM: [number + ' kuukautta', number + ' kuukauden'],
          y: ['vuosi', 'vuoden'],
          yy: [number + ' vuotta', number + ' vuoden'],
        };
        return !isFuture ? format[key][0] : format[key][1];
      }

      moment.locale('fi', {
        months: 'tammikuu_helmikuu_maaliskuu_huhtikuu_toukokuu_kesäkuu_heinäkuu_elokuu_syyskuu_lokakuu_marraskuu_joulukuu'.split(
          '_'
        ),
        monthsShort: 'tammi_helmi_maalis_huhti_touko_kesä_heinä_elo_syys_loka_marras_joulu'.split(
          '_'
        ),
        weekdays: 'sunnuntai_maanantai_tiistai_keskiviikko_torstai_perjantai_lauantai'.split(
          '_'
        ),
        weekdaysShort: 'su_ma_ti_ke_to_pe_la'.split('_'),
        weekdaysMin: 'su_ma_ti_ke_to_pe_la'.split('_'),
        longDateFormat: {
          LT: 'HH.mm',
          LTS: 'HH.mm.ss',
          L: 'DD.MM.YYYY',
          LL: 'Do MMMM[ta] YYYY',
          LLL: 'Do MMMM[ta] YYYY, [klo] LT',
          LLLL: 'dddd, Do MMMM[ta] YYYY, [klo] LT',
          l: 'D.M.YYYY',
          ll: 'Do MMM YYYY',
          lll: 'Do MMM YYYY, [klo] LT',
          llll: 'ddd, Do MMM YYYY, [klo] LT',
        },
        calendar: {
          sameDay: '[tänään] [klo] LT',
          nextDay: '[huomenna] [klo] LT',
          nextWeek: 'dddd [klo] LT',
          lastDay: '[eilen] [klo] LT',
          lastWeek: '[viime] dddd[na] [klo] LT',
          sameElse: 'L',
        },
        relativeTime: {
          future: '%s sitten',
          past: '%s sitten',
          s: 'hetki',
          m: processRelativeTime,
          mm: processRelativeTime,
          h: processRelativeTime,
          hh: processRelativeTime,
          d: processRelativeTime,
          dd: processRelativeTime,
          M: processRelativeTime,
          MM: processRelativeTime,
          y: processRelativeTime,
          yy: processRelativeTime,
        },
        ordinalParse: /\d{1,2}\./,
        ordinal: '%d.',
        week: {
          dow: 1, // Monday is the first day of the week.
          doy: 4, // The week that contains Jan 4th is the first week of the year.
        },
      });
    }
  };

  const get_new_messages = async () => {
    const element = document.querySelector('#chat_textarea');
    if (element && internal_uuid && partner_id) {
      await get_messages();
    }
  };

  // reactive functions ------------------------------------------------------- //

  $: $chat_count, get_new_messages();

  // initialization ----------------------------------------------------------- //

  const destroy_chat = e => {
    const element = document.querySelector('#chat_textarea');

    if (!window.location.pathname.includes('chat') && element) {
      element.removeEventListener(
        'keydown',
        set_send_message_with_enter_event,
        true
      );
      window.document.removeEventListener(
        'router:beforenavigation',
        destroy_chat
      );
    }
  };

  /**
   * the if external_order_uuid statement means you come from a notification or from an order
   * the else branch means you come from the side menu chat icon
   */
  const init_chat = async () => {
    set_moment_language();
    document
      .querySelector('.mdc-top-app-bar--fixed-adjust')
      .scrollIntoView({ behavior: 'smooth' });
    send_with_enter_value = $session.user.send_message_with_enter;
    if (send_with_enter_value === true) {
      document
        .querySelector('#chat_textarea')
        .addEventListener('keydown', set_send_message_with_enter_event, true);
    }
    message_list = document.querySelector('.chat-log');
    if (external_order_uuid) {
      external_partner = external_partner.split('&')[0];
      await open_chat(external_order_uuid, external_partner);
      show_partners = false;
    } else {
      input_placeholder = '';
      await show_partners_panel();
      disabled = true;
    }
    //setTimeout(set_moment_language, 100);

    window.document.addEventListener('router:beforenavigation', destroy_chat);
  };

  onMount(async () => {
    await init_chat();
  });
  onDestroy(() => destroy_chat());
</script>

<main class="wrapper" id="container-header">

  <div class="row">

    <!-- chat partners -->

    {#if show_partners}
      <div
        id="partners_container"
        class="column opacity partner-order-flex"
        style="max-height: 714px;">
        <div
          class="mdc-data-table partner-order-width"
          style="margin-right: 10px; border-top-left-radius: 4px;
          border-top-right-radius: 4px;">

          <!-- search box -->

          <div
            class="mdc-text-field mdc-text-field--outlined field-search fade-in"
            class:mdc-text-field--disabled={!search_disable || search_disable.length <= 0}
            style="margin: 10px 2px 10px 10px;">
            <input
              style="margin-left: 1px;"
              id="table-search-field"
              class="mdc-text-field__input"
              bind:value={searched}
              on:keyup={async e => {
                await filter_results(e);
              }} />
            <div class="mdc-notched-outline">
              <div class="mdc-notched-outline__leading" />
              <div class="mdc-notched-outline__notch">
                <label class="mdc-floating-label" for="table-search-field">
                  {search_label}
                </label>
              </div>
              <div class="mdc-notched-outline__trailing" />
            </div>
          </div>

          <!-- partners loop -->
          <table class="mdc-data-table__table">
            <tbody class="mdc-data-table__content">
              {#if !!all_chats && all_chats.length > 0}
                {#each all_chats as partner, i}
                  <tr
                    style="cursor: pointer;"
                    class="mdc-data-table__row"
                    in:fade={{ delay: 500 + i * 100, duration: 500 }}
                    on:click={async () => {
                      await open_chat(partner.order_uuid, partner.partner_id);
                    }}>
                    <span class="chat-message__avatar-frame">
                      <img
                        src={`${settings.api_host}${partner.image_url}`}
                        alt="avatar"
                        class="chat-message__avatar" />
                    </span>
                    <th class="mdc-data-table__cell">
                      {partner.name}
                      <br />
                      <span>
                        <i
                          class="material-icons"
                          style="display: inline-block; vertical-align: middle;
                          font-size: 12px; color:#8e8e8e"
                          aria-hidden="true">
                          file_copy
                        </i>
                        <p
                          style="display: inline-block; font-size: 12px;
                          font-weight: 100; font-style: italic">
                          {partner.order_description}
                        </p>
                      </span>
                      <br />
                      <span style="display:flex;">
                        {#if !!partner.last_message}
                          {#if !!partner.last_message.type}
                            {#if partner.last_message.type === 'text'}
                              <p style="font-size: 12px; font-weight: 100;">
                                {partner.last_message.text}
                              </p>
                            {:else if partner.last_message.type === 'image'}
                              <i
                                class="material-icons"
                                style="display: inline-block; margin-top: 4px;
                                vertical-align: middle; font-size: 12px;
                                color:#8e8e8e"
                                aria-hidden="true">
                                image
                              </i>
                              <p
                                style="font-size: 12px; font-weight: 100;
                                opacity: 50%; margin-left: 5px">
                                {image_label}
                              </p>
                            {/if}
                          {/if}
                        {/if}
                      </span>
                    </th>

                    <td
                      class="mdc-data-table__cell"
                      style="padding: 0; text-align: center; font-size: 13px;
                      font-weight: 100; max-width: 90px;">
                      {moment(partner.last_message_date)
                        .parseZone()
                        .fromNow()}
                      <br />
                      <span
                        class="unread-chat-count-badge"
                        style="margin-left: 50px"
                        class:hidden={!partner.unread}>
                        {partner.unread}
                      </span>
                    </td>
                  </tr>
                {/each}
              {:else}
                <div
                  style="text-align: center; margin-top: 15px; color: #616161;">
                  <p>{no_chats_yet_in_partner_panel}</p>
                </div>
              {/if}
            </tbody>
          </table>
        </div>

        <!-- chat partners pager -->
        <div
          class="page-row fade-in partner-order-width"
          class:opacity-half={disable_pager}
          style=" border-top-style: dashed; border-width: 1px; border-color:
          grey; display: flex; margin-bottom: -1px; height: 53px;
          background-color: #ffffff; border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px; ">
          {#if num_of_pages !== 0}
            <div class="page-column page-left">
              {#if current_page !== 1}
                <button
                  disabled={disable_pager}
                  on:click={async () => {
                    await prev_page();
                  }}
                  style="width: 90px; margin-left: 10px;"
                  class="mdc-button mdc-button--unelevated fade-in">
                  <span class="mdc-button__ripple" />
                  <span class="material-icons">keyboard_arrow_left</span>
                </button>
              {/if}
            </div>

            <div
              class="page-column page-middle fade-in"
              style="text-align: center; margin-top: 5px">
              {current_page}/{num_of_pages}
            </div>

            <div class="page-column page-right">
              {#if current_page !== num_of_pages}
                <button
                  disabled={disable_pager}
                  on:click={async () => {
                    await next_page();
                  }}
                  style="width: 90px; margin-left: -16px;"
                  class="mdc-button mdc-button--unelevated fade-in">
                  <span class="mdc-button__ripple" />
                  <span class="material-icons">keyboard_arrow_right</span>
                </button>
              {/if}
            </div>
          {/if}
        </div>
      </div>
    {:else}
      <!-- order summary -->
      <div
        class="column partner-order-flex order-mobile"
        in:fade={{ delay: 500, duration: 500 }}>
        <div class="partner-order-width" style="margin-right: 10px;">
          <button
            on:click={async () => {
              await show_partners_panel();
            }}
            style="margin-top: 0px; margin-bottom: 10px; width: 100%;"
            class="mdc-button mdc-button--unelevated order-back-button">
            <span class="mdc-button__ripple" />
            {goto_partners_lab}
          </button>

          <div
            id="order_status"
            style="width: 100%; background: white; padding: 2px 0 2px 0;
            border-top-right-radius: 2px; border-top-left-radius: 2px;">
            <th
              class="mdc-data-table__cell svelte-2sa3eq data-heading merge-cell">
              {order_status_label.replace(':', '')}
            </th>
            <td class="mdc-data-table__cell pre-wrap svelte-2sa3eq merge-cell">
              {order_status}
            </td>
          </div>

          {#if order}
            <div
              class="fade-in"
              style="max-height: 386px; overflow-y: auto;
              border-bottom-right-radius: 2px; border-bottom-left-radius: 2px;">
              <TableOrder
                hide={true}
                role={$session.role}
                bind:order
                chat={true} />
            </div>

            <button
              on:click={to_order}
              style="margin-top: 8px; width: 100%;"
              class="mdc-button mdc-button--unelevated order-back-button">
              <span class="mdc-button__ripple" />
              {goto_order_lab}
            </button>

            <!-- other workers -->

            {#if !!all_other_workers && all_other_workers.length > 1}
              <div
                style="background: white; padding: 2px 0 2px 0; margin-top: 8px;
                text-align: center; border-top-right-radius: 2px;
                border-top-left-radius: 2px;">
                <td
                  class="mdc-data-table__cell"
                  style="width: 326px; height: 41px; vertical-align:middle">
                  {other_workers_label}
                </td>
              </div>
              <div
                style="max-height: 132px; overflow-y: auto; overflow-x: hidden;
                border-bottom-right-radius: 2px; border-bottom-left-radius: 2px;">
                {#each all_other_workers as partner, i}
                  {#if partner.order_uuid === internal_uuid && partner.partner_id !== partner_id}
                    <div
                      class="mdc-data-table fade-in"
                      style="width: 334px; margin-right: 10px; max-height:
                      200px; overflow-y: scroll; height: unset;">
                      <table class="mdc-data-table__table">
                        <tbody class="mdc-data-table__content">
                          <tr
                            style="cursor: pointer;"
                            class="mdc-data-table__row"
                            on:click={async () => {
                              await open_chat(partner.order_uuid, partner.partner_id);
                            }}>
                            <span class="chat-message__avatar-frame">
                              <img
                                src={`${settings.api_host}${partner.image_url}`}
                                alt="avatar"
                                class="chat-message__avatar" />
                            </span>
                            <th class="mdc-data-table__cell">
                              {partner.name}
                              <br />
                              <span>
                                <i
                                  class="material-icons"
                                  style="display: inline-block; vertical-align:
                                  middle; font-size: 12px; color:#8e8e8e"
                                  aria-hidden="true">
                                  file_copy
                                </i>
                                <p
                                  style="display: inline-block; font-size: 12px;
                                  font-weight: 100; font-style: italic">
                                  {partner.order_description}
                                </p>
                              </span>
                              <br />
                              <span style="display:flex;">
                                {#if partner.last_message.text !== null}
                                  <p style="font-size: 12px; font-weight: 100;">
                                    {partner.last_message.text}
                                  </p>
                                {:else if partner.last_message.image_url !== null}
                                  <i
                                    class="material-icons"
                                    style="display: inline-block; margin-top:
                                    4px; vertical-align: middle; font-size:
                                    12px; color:#8e8e8e"
                                    aria-hidden="true">
                                    image
                                  </i>
                                  <p
                                    style="font-size: 12px; font-weight: 100;
                                    opacity: 50%; margin-left: 5px">
                                    {image_label}
                                  </p>
                                {/if}
                              </span>
                            </th>

                            <td
                              class="mdc-data-table__cell"
                              style="padding: 0; text-align: end; font-size:
                              13px; font-weight: 100; max-width: 90px;">
                              {moment(partner.last_message_date)
                                .parseZone()
                                .fromNow()}
                              <br />
                              <span
                                class="unread-chat-count-badge"
                                style="margin-left: 50px"
                                class:hidden={!partner.unread}>
                                {partner.unread}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  {/if}
                {/each}
              </div>
            {/if}
          {/if}

        </div>
      </div>
    {/if}

    <!-- chat section -->

    <div class="column chat-flex" class:order-mobile={!partner_profile}>

      <button
        on:click={async () => {
          await show_partners_panel();
        }}
        style="margin-top: 0px; margin-bottom: 10px; width: 100%; order: -1"
        class="mobile-button mdc-button mdc-button--unelevated order-back-button">
        <span class="mdc-button__ripple" />
        {goto_partners_lab}
      </button>

      {#if partner_profile}
        <div class="fade-in">
          {#if internal_uuid}
            <ChatBanner
              profile={message_partner_profile}
              order_uuid={internal_uuid}
              order_status={order_status_raw} />
          {:else}
            <ChatBanner profile={message_partner_profile} />
          {/if}
        </div>
      {:else}
        <table
          class="table order-mobile"
          style="height: 173px; border-radius: 5px; background: #ffffff9e;" />
      {/if}

      <section class="chat-container">

        {#if visible === false}
          <button
            id="goto_new_button"
            on:click={goto_message}
            style="margin: auto; padding: 4px; padding-left: 10px; margin-top:
            9px; font-size: 13px; padding-right: 10px; border-radius: 14px;
            opacity: 0.7; box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.3);
            cursor: pointer;"
            class="mdc-button mdc-button--unelevated fade-in">
            <span class="mdc-button__ripple" />
            {chat_goto_unread_label}
          </button>
        {/if}

        <div class="chat-log">
          {#if !!messages && messages.length === 0}
            <h3 class="chat-message__date">{no_chats_yet}</h3>
          {/if}
          {#each messages as message, i}
            {#if message.new_label === true && message.sender.id !== $session.user.id}
              <h3
                class="chat-message__date_new"
                class:message-group={message.group_position > 0}>
                <span style="float: right">{chat_new_label}</span>
                <span style="margin-left: 30px;">
                  {moment(message.created_at)
                    .parseZone()
                    .fromNow()}
                </span>
              </h3>
            {:else}
              <h3
                class="chat-message__date"
                class:message-group={message.group_position > 0}>
                {moment(message.created_at)
                  .parseZone()
                  .fromNow()}
              </h3>
            {/if}
            <div id="new-chat-message" class="chat-message-container">
              {#if message.group_position === 0 || message.group_position === undefined}
                <ChatMessage
                  last_role={$session.role}
                  avatar={message.sender.image_url}
                  role={message.sender.message_role}
                  message={message.message}
                  group={message.group}
                  sender_id={message.sender.id} />
              {:else}
                <ChatMessage
                  last_role={$session.role}
                  avatar={false}
                  role={message.sender.message_role}
                  message={message.message}
                  group={message.group}
                  sender_id={message.sender.id} />
              {/if}
            </div>
          {/each}
        </div>

        <!-- chat area -->

        <form class="chat-input-area" style="display: flex">
          <textarea
            {disabled}
            id="chat_textarea"
            style="border-bottom: 1px #00000038 solid;"
            placeholder={input_placeholder}
            class="chat-input-area__input"
            bind:value={message} />
          <button
            {disabled}
            id="send_button"
            type="submit"
            style="border-bottom: 1px #00000038 solid;"
            class="material-icons send-button"
            on:click|preventDefault={async () => {
              await send_message();
            }}>
            send
          </button>
        </form>

        <!-- chat - send with enter + upload image -->

        <div
          id="send_with_enter_section"
          class="enter-switcher"
          style="display: inline-block; text-align: end; opacity: 0.7; padding:
          3px; margin-top: -5px;">
          <div style="transform: scale(0.8, 0.8); margin-right: -50px;">

            <!-- upload image / picture -->
            <div
              style="display: inline-block;"
              class:disable-link={disabled}
              class:opacity-half={disabled}>
              <i style="position: relative; top: 5px;" class="material-icons">
                add_photo_alternate
              </i>
              <input
                class="bold-label"
                style="text-decoration: none; display: none; width: 100%;
                cursor: pointer; color: black; margin-right: 22px;"
                type="file"
                id="image_input"
                accept="image/*"
                on:change={() => load_file()} />
              <label
                style="cursor: pointer; margin-right: 10px;"
                for="image_input"
                class="bold-label">
                {chat_upload_image}
              </label>
            </div>

            <!-- chat enter  -->

            <div
              class="mdc-switch"
              class:mdc-switch--disabled={disabled}
              class:mdc-switch--checked={send_with_enter_value}
              on:click={async () => {
                await handle_switch_with_enter_click();
              }}>
              <div class="mdc-switch__track" />
              <div class="mdc-switch__thumb-underlay">
                <div class="mdc-switch__thumb" />
                <input
                  type="checkbox"
                  id="select_enter"
                  class="mdc-switch__native-control"
                  role="switch"
                  aria-checked="false" />
              </div>
            </div>
            <label
              class:opacity-half={disabled}
              style="margin-left: 10px;"
              for="select_enter"
              class="bold-label">
              {send_with_enter_label}
            </label>

          </div>
        </div>
      </section>
    </div>

    <!-- dialog section (if we need one) -->

    <div class="chat-dialog" />

  </div>
</main>

<div id="pic-dialog" class="mdc-dialog">
  <div class="mdc-dialog__container">
    <div
      class="mdc-dialog__surface"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="my-dialog-title"
      aria-describedby="my-dialog-content">

      <h2 class="mdc-dialog__title" id="img-title">{dialog_title}</h2>

      <div class="mdc-dialog__content">
        <img class="img-preview" id="img_preview" />
      </div>

      <footer class="mdc-dialog__actions">
        <button
          data-cy="close_pic"
          style="margin-right: 30px;"
          on:click={() => close_upload_dialog()}
          class="btn-close mdc-button mdc-button--unelevated">
          <span class="mdc-button__ripple" />
          {close_label}
        </button>

        <button
          on:click={() => start_cropping()}
          style="margin-right: 30px;"
          class="btn-close mdc-button mdc-button--unelevated">
          <span class="mdc-button__ripple" />
          {chat_start_cropping}
        </button>

        <button
          data-cy="send_pic"
          class="btn-close mdc-button mdc-button--unelevated"
          on:click|preventDefault={async () => {
            await post_chat_image();
          }}>
          <span class="mdc-button__ripple" />
          {chat_upload_image_button_label}
        </button>
      </footer>
    </div>
  </div>

  <div class="mdc-dialog__scrim" />
</div>

<style type="text/scss">
  /* Variables */
  $site-padding: 16px;
  $avatar-size: 54px;
  $submit-icon-size: 30px;

  /* Resets */
  p,
  form {
    margin: 0;
  }

  main {
    display: flex;
    flex-direction: column;
    margin-top: 3rem !important;
    padding: 0.5rem 0.5rem;
    background: #ffffff8c;
    border-radius: 10px;
  }

  .row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
  }

  .column {
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    flex: 1;
  }

  .opacity {
    opacity: 0;
  }

  .opacity-half {
    opacity: 0.5;
  }

  .merge-cell {
    display: block;
    height: 100%;
    text-align: center;
    max-width: 334px;
  }

  .pre-wrap {
    white-space: pre-wrap !important;
  }

  .chat-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
    max-width: 800px;
    /*height: 55vh;*/
    height: 534px;
    background: #eef0f0e3;
    box-shadow: 2px 1px 10px rgba(#a9a6a6, 0.4);
    margin: 0 auto;
    margin-top: 8px;
    border-radius: 8px;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  .chat-message__avatar-frame {
    display: flex;
    align-items: center;
    justify-content: center;
    width: $avatar-size;
    height: $avatar-size;
    border-radius: 50%;
    overflow: hidden;
    margin-right: 0px;
    margin-left: 16px;
    margin-top: 5px;
  }

  .pointer {
    cursor: pointer;
  }

  .cropper-containe {
    width: 511px;
  }

  .chat-message__avatar {
    height: 100%;
  }

  /* Chat log*/
  .chat-log {
    box-sizing: border-box;
    padding: $site-padding;
    overflow-y: auto;
  }

  /* Chat input area */
  .chat-input-area {
    position: relative;
  }

  .chat-input-area__input {
    font-family: Roboto, sans-serif;
    font-size: 14px;
    width: 100%;
    background: #fff;
    border: none;
    box-sizing: border-box;
    padding: 32px;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;

    &:focus {
      outline: none;
    }
  }

  textarea {
    width: 100%;
    height: 100px;
    resize: none;
  }

  .send-button {
    border: none;
    background: #fff;
    outline: none;
    cursor: pointer;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    margin: 0;
  }

  button:disabled {
    cursor: not-allowed;
    pointer-events: all !important;
  }

  .disable-link {
    pointer-events: none;
    cursor: default;
  }

  .chat-message__date {
    margin-top: 8px;
    font-size: 0.8em;
    font-weight: normal;
    text-align: center;
    color: #727b80;
  }

  .chat-message__date_new {
    margin-top: 8px;
    font-size: 0.8em;
    font-weight: normal;
    text-align: center;
    color: #727b80;
    border-bottom: 1px solid #d4d4d5;
    scroll-margin-top: 100px;
  }

  .page-column {
    float: left;
    padding: 10px 0 10px 0;
  }

  .page-left,
  .page-right {
    width: 25%;
  }

  .page-middle {
    width: 50%;
    color: #616161;
  }

  .page-row:after {
    content: '';
    display: table;
    clear: both;
  }

  .message-group {
    display: none;
  }

  /* Chat partners */

  .unread-chat-count-badge {
    margin-top: 6px;
    display: block;
    min-width: 17px;
    //top: 10px;
    //left: 20px;
    background-color: #069;
    color: white;
    border-radius: 50%;
    width: 17px;
    height: 17px;
    font-size: 11px;
    font-weight: bold;
    text-align: center;
    padding: 0;
    overflow: hidden;
    line-height: 20px;
    margin-left: 32px;
  }

  .hidden {
    display: none;
  }

  .font-order-status-label {
    color: #616161;
    font-weight: 800;
    text-align: left;
    max-width: 300px;
    min-width: 150px;
    word-wrap: break-word;
    white-space: normal;
    font-size: 0.875rem;
  }

  .font-order-status {
    font-style: italic;
    font-family: Roboto, sans-serif;
    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 400;
    letter-spacing: 0.0178571429em;
    text-transform: inherit;
    color: rgba(0, 0, 0, 0.5);
    text-align: left;
  }

  .mdc-data-table__cell {
    color: #616161;
  }

  #unread- {
    scroll-margin-top: 20px;
  }

  .cell-right {
    max-width: 200px;
    word-wrap: break-word;
    white-space: normal;
  }

  .mdc-data-table {
    width: 98%;
    border: 0;
    border-radius: 0;
    overflow-x: auto;
    height: 662px;
    overflow-y: scroll;
  }

  .mdc-data-table__row {
    border-top-style: dashed;
  }

  th {
    font-weight: 800;
    text-align: left;
    max-width: 166px;
    min-width: 100px;
  }

  td {
    width: 100%;
    font-weight: 500;
  }

  .chat-flex {
    flex: 0.75;
    margin-top: 0;
  }

  .partner-order-width {
    width: 334px;
  }

  .order-mobile {
    display: block;
  }

  /* Desktop Mode
  *******************************************************/
  @media (min-width: 1025px) {
    .wrapper {
      width: 900px;
      margin: 0 auto;
      margin-bottom: 50px;
      height: fit-content;
    }

    .mobile-button {
      display: none;
    }

    .partner-order-flex {
      flex: 0.25;
    }

    .enter-switcher {
      display: block;
    }
  }

  /* Tablet Mode
  *******************************************************/
  @media (min-width: 769px) and (max-width: 1024px) {
    .wrapper {
      width: 80%;
      margin: 0 auto;
      margin-left: 85px;
      margin-bottom: 50px;
      height: fit-content;
    }

    .mdc-top-app-bar--fixed-adjust {
      margin-right: -24px;
    }

    .mobile-button {
      display: none;
    }

    .partner-order-flex {
      flex: 0.25;
    }

    .enter-switcher {
      display: none;
    }
  }

  /* Mobile Mode
  *******************************************************/
  @media (min-width: 0) and (max-width: 768px) {
    .wrapper {
      /*width: 100%;*/
      margin: 0 auto;
      margin-left: 50px;
      margin-bottom: 50px;
      height: fit-content;
    }

    .chat-input-area__input {
      padding: 0;
      height: 50px;
      padding-top: 10px;
      padding-left: 6px;
    }

    .chat-flex {
      flex: unset;
      margin-top: 10px;
    }

    .partner-order-flex {
      flex: unset;
      width: 100%;
    }

    .order-mobile {
      display: none;
    }

    .partner-order-width {
      width: 100%;
    }

    .mobile-button {
      display: block;
    }

    .enter-switcher {
      display: none;
    }
  }
</style>
